import React from 'react';
import { Form, Input, Space, Button, Tag } from 'antd';

interface ChangeRequest {
  field_name: string;
  status: string;
  old_value?: string;
  new_value?: string;
  id: number;
}

export interface CreateBusinessEmployerData {
  employer_no?: string;
  employer_no_password?: string;
  sst_id?: string;
  sst_password?: string;
  kwsp_id?: string;
  kwsp_password?: string;
  perkeso_id?: string;
  perkeso_password?: string;
  hrdc_id?: string;
  hrdc_password?: string;
  pcb?: string;
}

interface EmployersTabProps {
  employer: CreateBusinessEmployerData;
  updateEmployer: (field: keyof CreateBusinessEmployerData, value: string) => void;
  isEdit: boolean;
  isEditing?: boolean;
  isCustomerPortal?: boolean;
  changeRequests?: ChangeRequest[];
  getEmployerDisplayValue?: (field: keyof CreateBusinessEmployerData) => string;
  handleEmployerFieldChange?: (field: keyof CreateBusinessEmployerData, value: string) => void;
  handleApproveChangeRequest?: (requestId: number) => void;
  handleRejectChangeRequest?: (requestId: number) => void;
}

export const BusinessEmployersTab: React.FC<EmployersTabProps> = ({
  employer,
  updateEmployer,
  isEdit,
  isEditing = false,
  isCustomerPortal = false,
  changeRequests = [],
  getEmployerDisplayValue,
  handleEmployerFieldChange,
  handleApproveChangeRequest,
  handleRejectChangeRequest,
}) => {
  // In create mode (!isEdit), always allow editing. In edit mode, use isEditing state.
  const canEdit = !isEdit || isEditing;

  const getFieldCR = (field: keyof CreateBusinessEmployerData) =>
    changeRequests.find((cr) => cr.status === 'pending' && cr.field_name === `employer_${field}`);

  const renderValue = (field: keyof CreateBusinessEmployerData, value?: string, password?: boolean) => {
    const cr = getFieldCR(field);
    if (canEdit) {
      let display = value || '';
      if (cr?.new_value) display = cr.new_value;
      else if (getEmployerDisplayValue) {
        const pending = getEmployerDisplayValue(field);
        if (pending !== '') display = pending;
      }
      return (
        <Input
          type="text"
          value={display}
          onChange={(e) => {
            const val = e.target.value;
            if (handleEmployerFieldChange) handleEmployerFieldChange(field, val);
            else updateEmployer(field, val);
          }}
          placeholder={password ? 'Password' : undefined}
        />
      );
    }
    if (cr) {
      if (cr.status === 'approved') return <span>{cr.new_value || value || '-'}</span>;
      if (cr.status === 'rejected') return <span>{cr.old_value || value || '-'}</span>;
      return (
        <div>
          <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
            {cr.old_value || value || '-'}
          </span>
          <span style={{ color: '#ff9800' }}>{cr.new_value || '-'}</span>
        </div>
      );
    }
    return <span>{value || '-'}</span>;
  };

  const renderAction = () => {
    const pending = changeRequests.filter((cr) => cr.status === 'pending' && cr.field_name.startsWith('employer_'));
    if (pending.length === 0) return null;
    if (isCustomerPortal) return <Tag color="orange">Pending</Tag>;
    return (
      <Space>
        {pending.map((cr) => (
          <React.Fragment key={cr.id}>
            <Button
              type="primary"
              size="small"
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              onClick={() => handleApproveChangeRequest && handleApproveChangeRequest(cr.id)}
            >
              Approve
            </Button>
            <Button
              danger
              size="small"
              onClick={() => handleRejectChangeRequest && handleRejectChangeRequest(cr.id)}
            >
              Reject
            </Button>
          </React.Fragment>
        ))}
      </Space>
    );
  };

  return (
    <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
            <tr style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
              <th style={{ padding: '12px', width: '20%', textAlign: 'left', borderRight: '1px solid #f0f0f0' }}>Employer</th>
              <th style={{ padding: '12px', width: '20%', textAlign: 'left', borderRight: '1px solid #f0f0f0' }}>SST</th>
              <th style={{ padding: '12px', width: '20%', textAlign: 'left', borderRight: '1px solid #f0f0f0' }}>KWSP</th>
              <th style={{ padding: '12px', width: '20%', textAlign: 'left', borderRight: '1px solid #f0f0f0' }}>PERKESO</th>
              <th style={{ padding: '12px', width: '20%', textAlign: 'left' }}>HRDC</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
              <Space direction="vertical" style={{ width: '100%' }} size={6}>
              {renderValue('employer_no', employer.employer_no)}
                {renderValue('employer_no_password', employer.employer_no_password, true)}
              </Space>
            </td>
            <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
              <Space direction="vertical" style={{ width: '100%' }} size={6}>
                {renderValue('sst_id', employer.sst_id)}
                {renderValue('sst_password', employer.sst_password, true)}
              </Space>
            </td>
            <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
              <Space direction="vertical" style={{ width: '100%' }} size={6}>
                {renderValue('kwsp_id', employer.kwsp_id)}
                {renderValue('kwsp_password', employer.kwsp_password, true)}
              </Space>
            </td>
            <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
              <Space direction="vertical" style={{ width: '100%' }} size={6}>
                {renderValue('perkeso_id', employer.perkeso_id)}
                {renderValue('perkeso_password', employer.perkeso_password, true)}
              </Space>
            </td>
            <td style={{ padding: '12px' }}>
              <Space direction="vertical" style={{ width: '100%' }} size={6}>
                {renderValue('hrdc_id', employer.hrdc_id)}
                {renderValue('hrdc_password', employer.hrdc_password, true)}
              </Space>
            </td>
          </tr>
          <tr>
            <td colSpan={5} style={{ padding: '12px' }}>
              {renderAction()}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
